/**
 * Generate rental credentials email HTML from template configuration
 * @param {Object} templateConfig - Template configuration from EmailTemplate model
 * @param {Object} data - Email data (name, code, dates, etc.)
 * @param {string} language - Language code ('es' or 'en')
 * @returns {string} HTML email content
 */
export function generateRentalCredentialsEmail(templateConfig, data, language = 'es') {
  const {
    name,
    code,
    startDate,
    endDate,
    zoneNumber,
    zoneDescription,
    lockersInfoHtml,
    zoneLink
  } = data;

  const config = {
    // Header - prioritize base64 over URL
    headerImageUrl: templateConfig.header_image_base64 || templateConfig.header_image_url || 'https://vacway-images.s3.eu-central-1.amazonaws.com/header_email.png',
    headerLink: templateConfig.header_link || 'www.vacway.com',
    headerBgColor: templateConfig.header_bg_color || '#022040',
    
    // Colors
    primaryColor: templateConfig.primary_color || '#022040',
    secondaryColor: templateConfig.secondary_color || '#000000',
    backgroundColor: templateConfig.background_color || '#eee6e6',
    textColor: templateConfig.text_color || '#3e3e3e',
    
    // Typography
    fontFamily: templateConfig.font_family || 'Roboto, Arial',
    
    // Texts (language-specific) - access nested language objects
    greetingText: ((templateConfig[language] || templateConfig.es || {}).greeting_text || '¡Hola {name}!').replace('{name}', name),
    introText: (templateConfig[language] || templateConfig.es || {}).intro_text || 'A continuación te proporcionamos los detalles de tu reserva y los códigos para acceder a las taquillas:',
    footerInfoText: (templateConfig[language] || templateConfig.es || {}).footer_info_text || 'Recuerda que dispones de tu taquilla hasta las 23:59 del día de fin de tu reserva.',
    footerContactText: (templateConfig[language] || templateConfig.es || {}).footer_contact_text || 'Para cualquier duda, ponte en contacto con nosotros a través de support@vacway.com',
    legalText: (templateConfig[language] || templateConfig.es || {}).legal_text || '',
    companyInfo: templateConfig.company_info || 'VACWAY TECH SL',
    privacyPolicyUrl: templateConfig.privacy_policy_url || 'https://vacway.com/locker-terms.html',
    
    // Social
    socialInstagram: templateConfig.social_instagram,
    socialFacebook: templateConfig.social_facebook,
    socialTiktok: templateConfig.social_tiktok,
    socialLinkedin: templateConfig.social_linkedin,
    socialTwitter: templateConfig.social_twitter,
    socialBgColor: templateConfig.social_bg_color || '#000000'
  };

  return `<!DOCTYPE html>
<html>
<head>
  <meta content="text/html charset=utf-8" http-equiv="Content-Type" />
  <meta content="width=device-width, initial-scale=1.0" name="viewport" />
  <meta name="x-apple-disable-message-reformatting" />
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <style>
    ${getEmailStyles(config)}
  </style>
</head>
<body leftmargin="0" rightmargin="0" topmargin="0" marginheight="0" offset="0" align="center">
  <table class="email-template" bgcolor="${config.backgroundColor}" style="background:${config.backgroundColor}; width: 100%;" align="center" border="0" cellpadding="0" cellspacing="0">
    <tbody>
      <!-- Header -->
      ${generateHeader(config)}
      
      <!-- Body -->
      ${generateBody(config, { code, startDate, endDate, zoneNumber, zoneDescription, lockersInfoHtml, zoneLink })}
      
      <!-- Footer -->
      ${generateFooter(config)}
    </tbody>
  </table>
</body>
</html>`;
}

function getEmailStyles(config) {
  return `
    * { box-sizing: border-box; }
    body { margin: 0; padding: 0; font-family: ${config.fontFamily}; }
    table { border-collapse: collapse !important; border-spacing: 0; margin: 0; }
    img { border: 0; line-height: 100%; margin: auto; text-align: center; max-width: 100%; height: auto; }
    
    p, .text {
      color: ${config.textColor};
      font-family: ${config.fontFamily};
      font-size: 16px;
      line-height: 150%;
      margin: 0px;
    }
    
    a {
      color: ${config.primaryColor};
      text-decoration: none;
    }
    
    .mobile-collapse { width: 100%; }
    
    @media only screen and (max-width: 480px) {
      body { min-width: 100% !important; width: 100% !important; }
      .mobile-collapse { max-width: 100% !important; min-width: 100% !important; width: 100% !important; }
      .hide-on-mobile { display: none !important; }
    }
  `;
}

function generateHeader(config) {
  return `
    <tr>
      <td class="mobile-collapse" align="center" style="width: 100%;">
        <table border="0" cellpadding="0" cellspacing="0" align="center" class="mobile-collapse" style="width: 100%; min-height: 150px;">
          <tbody>
            <tr>
              <td align="center" class="mobile-collapse">
                <table border="0" cellpadding="0" cellspacing="0" width="650" class="mobile-collapse" style="width: 650px;">
                  <tbody>
                    <tr>
                      <td align="center" width="650" class="mobile-collapse">
                        <table border="0" cellpadding="0" cellspacing="0" height="150" width="650" bgcolor="#ffffff" style="background-color: #ffffff; min-height: 150px;" class="mobile-collapse">
                          <tbody>
                            <tr>
                              <th height="150" width="650" bgcolor="${config.headerBgColor}" class="mobile-collapse" style="background-color: ${config.headerBgColor}; vertical-align: middle;">
                                <table border="0" cellpadding="0" cellspacing="0" height="150" class="mobile-collapse" style="min-height: 150px; width: 650px;">
                                  <tbody>
                                    <tr>
                                      <td class="mobile-collapse" height="150" style="width: 650px; vertical-align: middle; padding: 0px;">
                                        <table border="0" cellpadding="0" cellspacing="0" width="650" class="mobile-collapse" style="width: 650px;">
                                          <tbody>
                                            <tr>
                                              <td class="mobile-collapse" valign="top" align="center">
                                                <table border="0" cellspacing="0" cellpadding="0" width="100%">
                                                  <tr>
                                                    <td align="center" style="padding: 0px;">
                                                      <a href="${config.headerLink}" target="_blank">
                                                        <img src="${config.headerImageUrl}" alt="" width="650" style="display: block; width: 650px; max-width: 100%;" />
                                                      </a>
                                                    </td>
                                                  </tr>
                                                </table>
                                              </td>
                                            </tr>
                                          </tbody>
                                        </table>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </th>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          </tbody>
        </table>
      </td>
    </tr>
  `;
}

function generateBody(config, data) {
  const { code, startDate, endDate, zoneNumber, zoneDescription, lockersInfoHtml, zoneLink } = data;
  
  return `
    <tr>
      <td class="mobile-collapse" align="center" style="width: 100%;">
        <table border="0" cellpadding="0" cellspacing="0" align="center" class="mobile-collapse" style="width: 100%;">
          <tbody>
            <tr>
              <td align="center" class="mobile-collapse">
                <table border="0" cellpadding="0" cellspacing="0" width="650" class="mobile-collapse" style="width: 650px;">
                  <tbody>
                    <!-- Greeting Section -->
                    <tr>
                      <td align="center" width="650" class="mobile-collapse">
                        <table border="0" cellpadding="0" cellspacing="0" width="650" bgcolor="#ffffff" style="background-color: #ffffff; padding: 20px 0;" class="mobile-collapse">
                          <tbody>
                            <tr>
                              <th width="650" bgcolor="#ffffff" class="mobile-collapse" style="background-color: #ffffff; vertical-align: middle;">
                                <table border="0" cellpadding="0" cellspacing="0" class="mobile-collapse" style="width: 650px;">
                                  <tbody>
                                    <tr>
                                      <td class="mobile-collapse" style="width: 650px; padding: 10px 50px;">
                                        <table border="0" cellpadding="0" cellspacing="0" style="width: 100%;">
                                          <tr>
                                            <td style="padding: 5px;">
                                              <p style="text-align: center; line-height: 150%; color: ${config.textColor}; font-family: ${config.fontFamily}; font-size: 18px;">
                                                ${config.greetingText}
                                              </p>
                                              <p style="text-align: center; line-height: 150%; color: ${config.textColor}; font-family: ${config.fontFamily}; font-size: 18px; margin-top: 15px;">
                                                ${config.introText}
                                              </p>
                                            </td>
                                          </tr>
                                        </table>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </th>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                    
                    <!-- Details Section -->
                    <tr>
                      <td align="center" width="650" class="mobile-collapse">
                        <table border="0" cellpadding="0" cellspacing="0" width="650" bgcolor="#ffffff" style="background-color: #ffffff; margin-top: 10px;" class="mobile-collapse">
                          <tbody>
                            <tr>
                              <th width="650" bgcolor="${config.primaryColor}" class="mobile-collapse" style="background-color: ${config.primaryColor}; vertical-align: top;">
                                <table border="0" cellpadding="0" cellspacing="0" class="mobile-collapse" style="width: 650px;">
                                  <tbody>
                                    <tr>
                                      <td class="mobile-collapse" style="width: 650px; padding: 25px 50px;">
                                        <table border="0" cellpadding="0" cellspacing="0" style="width: 100%;">
                                          <tr>
                                            <td style="padding: 5px;">
                                              <p style="color: #ffffff; font-family: ${config.fontFamily}; font-size: 18px; line-height: 27px;">
                                                NÚMERO DE RESERVA: <strong>${code}</strong>
                                              </p>
                                              <br>
                                              <p style="color: #ffffff; font-family: ${config.fontFamily}; font-size: 18px; line-height: 150%;">
                                                FECHA DE INICIO: <strong>${startDate}</strong> - FECHA DE FIN: <strong>${endDate}</strong>
                                              </p>
                                              <br>
                                              <p>
                                                <a href="${zoneLink}" style="color: #ffffff; font-family: ${config.fontFamily}; font-size: 18px; line-height: 150%; cursor: pointer;">
                                                  TAQUILLAS ZONA: <strong>${zoneNumber} - ${zoneDescription}</strong>
                                                </a>
                                              </p>
                                              <br>
                                              ${lockersInfoHtml}
                                            </td>
                                          </tr>
                                        </table>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </th>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          </tbody>
        </table>
      </td>
    </tr>
  `;
}

function generateFooter(config) {
  const socialButtons = generateSocialButtons(config);
  
  return `
    <tr>
      <td class="mobile-collapse" align="center" style="width: 100%;">
        <table border="0" cellpadding="0" cellspacing="0" align="center" class="mobile-collapse" style="width: 100%;">
          <tbody>
            <tr>
              <td align="center" class="mobile-collapse">
                <table border="0" cellpadding="0" cellspacing="0" width="650" class="mobile-collapse" style="width: 650px;">
                  <tbody>
                    <!-- Info Text -->
                    <tr>
                      <td align="center" width="650" class="mobile-collapse">
                        <table border="0" cellpadding="0" cellspacing="0" width="650" bgcolor="#ffffff" style="background-color: #ffffff; padding: 15px 0;" class="mobile-collapse">
                          <tbody>
                            <tr>
                              <th width="650" bgcolor="#ffffff" class="mobile-collapse" style="background-color: #ffffff;">
                                <table border="0" cellpadding="0" cellspacing="0" class="mobile-collapse" style="width: 650px;">
                                  <tbody>
                                    <tr>
                                      <td class="mobile-collapse" style="width: 650px; padding: 15px 50px;">
                                        <table border="0" cellpadding="0" cellspacing="0" style="width: 100%;">
                                          <tr>
                                            <td style="padding: 5px;">
                                              <p style="text-align: justify; line-height: 150%; color: ${config.textColor}; font-family: ${config.fontFamily};">
                                                ${config.footerInfoText}
                                              </p>
                                              <br>
                                              <p style="text-align: justify; color: ${config.textColor}; font-family: ${config.fontFamily};">
                                                ${config.footerContactText}
                                              </p>
                                            </td>
                                          </tr>
                                        </table>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </th>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                    
                    <!-- Divider -->
                    <tr>
                      <td align="center" width="650" class="mobile-collapse">
                        <table border="0" cellpadding="0" cellspacing="0" width="650" bgcolor="#ffffff" style="background-color: #ffffff;" class="mobile-collapse">
                          <tbody>
                            <tr>
                              <td style="padding: 0;">
                                <table cellpadding="0" cellspacing="0" width="100%" style="border-bottom: 1px solid ${config.secondaryColor};">
                                  <tbody>
                                    <tr><td></td></tr>
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                    
                    <!-- Social & Company Info -->
                    <tr>
                      <td align="center" width="650" class="mobile-collapse">
                        <table border="0" cellpadding="0" cellspacing="0" width="650" bgcolor="${config.socialBgColor}" style="background-color: ${config.socialBgColor}; padding: 20px 0;" class="mobile-collapse">
                          <tbody>
                            <tr>
                              <th width="650" class="mobile-collapse" style="vertical-align: top;">
                                <table border="0" cellpadding="0" cellspacing="0" class="mobile-collapse" style="width: 650px;">
                                  <tbody>
                                    <tr>
                                      <td class="mobile-collapse" style="width: 650px; padding: 10px;">
                                        ${socialButtons}
                                        <table border="0" cellpadding="0" cellspacing="0" style="width: 100%; margin-top: 15px;">
                                          <tr>
                                            <td style="padding: 5px;">
                                              <p style="text-align: center; color: #ffffff; font-family: ${config.fontFamily}; font-size: 18px; line-height: 150%;">
                                                ${config.companyInfo.replace(/\n/g, '<br>')}
                                              </p>
                                            </td>
                                          </tr>
                                        </table>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </th>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                    
                    <!-- Legal Text -->
                    <tr>
                      <td align="center" width="650" class="mobile-collapse">
                        <table border="0" cellpadding="0" cellspacing="0" width="650" bgcolor="#ffffff" style="background-color: #ffffff; padding: 20px 0;" class="mobile-collapse">
                          <tbody>
                            <tr>
                              <th width="650" class="mobile-collapse">
                                <table border="0" cellpadding="0" cellspacing="0" class="mobile-collapse" style="width: 650px;">
                                  <tbody>
                                    <tr>
                                      <td class="mobile-collapse" style="width: 650px; padding: 15px;">
                                        <table border="0" cellpadding="0" cellspacing="0" style="width: 100%;">
                                          <tr>
                                            <td style="padding: 5px;">
                                              <p style="color: #bababa; font-family: ${config.fontFamily}; font-size: 13px; text-align: center; line-height: 150%;">
                                                ${config.legalText.replace(/\n\n/g, '<br><br>')}
                                                ${config.privacyPolicyUrl ? `<br><br><a href="${config.privacyPolicyUrl}" style="color: #1155cc;" target="_blank">Política Privacidad</a>` : ''}
                                              </p>
                                            </td>
                                          </tr>
                                        </table>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </th>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          </tbody>
        </table>
      </td>
    </tr>
  `;
}

function generateSocialButtons(config) {
  const socials = [
    { url: config.socialInstagram, icon: 'instagram' },
    { url: config.socialFacebook, icon: 'facebook' },
    { url: config.socialTiktok, icon: 'tiktok' },
    { url: config.socialLinkedin, icon: 'linkedin' },
    { url: config.socialTwitter, icon: 'twitter' }
  ].filter(s => s.url);

  if (socials.length === 0) return '';

  const buttons = socials.map(social => `
    <td style="padding: 5px; display: inline-block;">
      <table border="0" cellpadding="0" cellspacing="0">
        <tbody>
          <tr>
            <td bgcolor="${config.socialBgColor}" style="border-radius: 3px; background: ${config.socialBgColor}; padding: 5px;">
              <a href="${social.url}" target="_blank">
                <img style="width: 34px; height: 34px;" width="34" height="34" src="https://s3.amazonaws.com/angular-assets/emailBuilder/social-buttons/color/${social.icon}.png" alt="${social.icon}" />
              </a>
            </td>
          </tr>
        </tbody>
      </table>
    </td>
  `).join('');

  return `
    <table border="0" cellspacing="0" cellpadding="0" class="social-buttons" style="display: block; margin: 0 auto; text-align: center;">
      <tbody>
        <tr>
          <td style="padding: 15px; text-align: center;">
            <table border="0" cellspacing="0" cellpadding="0" style="margin: 0 auto;">
              <tr>
                ${buttons}
              </tr>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  `;
}

export default generateRentalCredentialsEmail;
